import { useEffect, useState } from "react";
import { apiFetch } from "../../api";
import { BASE_URL as u } from "../../../commonUrls";

const SUGGEST_URL = `${u}/transaction/categorize-suggestions`;
const APPLY_URL   = `${u}/transaction/apply-categories`;
const CAT_URL     = `${u}/category`;

const SOURCE_LABEL = { MERCHANT_MATCH: "Matched", RULE: "Rule", AI: "AI", NONE: "" };
const SOURCE_COLOR = { MERCHANT_MATCH: "#22c55e", RULE: "#f59e0b", AI: "#8b5cf6", NONE: "#6b7280" };
const CONF_COLOR   = { HIGH: "#22c55e", MEDIUM: "#f59e0b", LOW: "#f87171" };

export default function CategorizationModal({ onClose }) {
  const [loading, setLoading]       = useState(true);
  const [applying, setApplying]     = useState(false);
  const [data, setData]             = useState(null);
  const [categories, setCategories] = useState([]);
  // overrides: { [transactionId]: { categoryId, merchantId, merchantName } }
  const [overrides, setOverrides]   = useState({});
  const [filter, setFilter]         = useState("all");   // all | suggested | unseen
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState(null);

  useEffect(() => {
    Promise.all([fetchSuggestions(), fetchCategories()]);
  }, []);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(SUGGEST_URL);
      const json = await res.json();
      setData(json);
      // Auto-seed overrides from high/medium confidence suggestions
      const initial = {};
      (json.rows || []).forEach(row => {
        if (row.suggestedCategoryId &&
            (row.enrichmentConfidence === "HIGH" || row.enrichmentConfidence === "MEDIUM")) {
          initial[row.transactionId] = {
            categoryId:   row.suggestedCategoryId,
            merchantId:   null,
            merchantName: row.merchantHint || null,
          };
        }
      });
      setOverrides(initial);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await apiFetch(CAT_URL);
      if (res.ok) setCategories(await res.json());
    } catch (_) {}
  };

  const setRowCategory = (txId, categoryId) => {
    setOverrides(prev => ({
      ...prev,
      [txId]: { ...(prev[txId] || {}), categoryId: categoryId || null }
    }));
  };

  const setRowMerchant = (txId, merchantName) => {
    setOverrides(prev => ({
      ...prev,
      [txId]: { ...(prev[txId] || {}), merchantId: null, merchantName }
    }));
  };

  const acceptAll = () => {
    const next = { ...overrides };
    (data?.rows || []).forEach(row => {
      if (row.suggestedCategoryId) {
        next[row.transactionId] = {
          categoryId:   row.suggestedCategoryId,
          merchantId:   null,
          merchantName: row.merchantHint || null,
        };
      }
    });
    setOverrides(next);
  };

  const clearAll = () => setOverrides({});

  const handleApply = async () => {
    const payload = {};
    Object.entries(overrides).forEach(([txId, ov]) => {
      if (ov.categoryId || ov.merchantId || ov.merchantName) {
        payload[txId] = ov;
      }
    });
    if (Object.keys(payload).length === 0) return;
    setApplying(true);
    try {
      const res = await apiFetch(APPLY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      setResult(json.applied ?? Object.keys(payload).length);
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────
  const rows = data?.rows ?? [];
  const confirmedCount = Object.values(overrides).filter(o => o.categoryId).length;

  const filteredRows = rows.filter(row => {
    if (filter === "suggested") return !!row.suggestedCategoryId;
    if (filter === "unseen")    return !row.suggestedCategoryId;
    return true;
  });

  // ── Result screen ─────────────────────────────────────────────
  if (result != null) {
    return (
      <div style={overlay} onClick={onClose}>
        <div style={modalBox} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
            <h3 style={{ margin: "0 0 8px" }}>Done</h3>
            <p style={{ color: "var(--text-muted)", margin: "0 0 24px" }}>
              {result} transaction{result !== 1 ? "s" : ""} updated.
            </p>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <h3 style={{ margin: 0 }}>Categorize Transactions</h3>
            {data && (
              <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
                {data.totalUncategorized} uncategorized &nbsp;·&nbsp;
                <span style={{ color: "#8b5cf6" }}>{data.autoSuggested} suggestions from AI / rules</span>
              </div>
            )}
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {error && (
          <div style={{ color: "#f87171", background: "rgba(239,68,68,0.08)", border: "1px solid #ef4444",
            borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text-muted)" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
            Analysing transactions with AI…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Stats bar */}
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 14,
              padding: "10px 14px", background: "rgba(255,255,255,0.04)", borderRadius: 8 }}>
              <Stat label="Total uncategorized" value={data.totalUncategorized} color="#6b7280" />
              <Stat label="AI/Rule suggested"   value={data.autoSuggested}      color="#8b5cf6" />
              <Stat label="Confirmed"           value={confirmedCount}           color="#22c55e" />
              <Stat label="Still pending"       value={data.totalUncategorized - confirmedCount} color="#f59e0b" />
            </div>

            {/* Toolbar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {["all", "suggested", "unseen"].map(f => (
                  <button key={f} onClick={() => setFilter(f)}
                    style={{ ...filterBtn, ...(filter === f ? filterBtnActive : {}) }}>
                    {f === "all" ? "All" : f === "suggested" ? "Has suggestion" : "Needs review"}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={acceptAll}>Accept all</button>
                <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={clearAll}>Clear all</button>
              </div>
            </div>

            {/* Transaction rows */}
            {filteredRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-muted)" }}>
                No transactions in this filter.
              </div>
            ) : (
              <div style={{ maxHeight: 420, overflowY: "auto", borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.08)" }}>
                {filteredRows.map((row, i) => {
                  const ov = overrides[row.transactionId] || {};
                  const catId = ov.categoryId !== undefined ? ov.categoryId : row.suggestedCategoryId;
                  const merchant = ov.merchantName !== undefined ? ov.merchantName : (row.merchantHint || "");
                  const isConfirmed = !!catId;

                  return (
                    <div key={row.transactionId} style={{
                      display: "grid", gridTemplateColumns: "1fr auto", gap: 12,
                      padding: "10px 14px",
                      borderBottom: i < filteredRows.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                      background: isConfirmed ? "rgba(34,197,94,0.03)" : "transparent"
                    }}>
                      {/* Left: transaction info + enrichment */}
                      <div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                            {row.date}
                          </span>
                          <span style={{ fontSize: 13 }} title={row.description}>
                            {truncate(row.description, 60)}
                          </span>
                          <span style={{
                            fontWeight: 700, whiteSpace: "nowrap", fontSize: 13,
                            color: row.type === "INCOME" ? "#22c55e" : "#f87171"
                          }}>
                            {row.type === "INCOME" ? "+" : "−"}
                            ₹{Number(row.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </div>

                        {/* Merchant + source badge */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                          <MerchantInput
                            value={merchant}
                            onChange={v => setRowMerchant(row.transactionId, v)}
                          />
                          {row.enrichmentSource && row.enrichmentSource !== "NONE" && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, flexShrink: 0,
                              color: SOURCE_COLOR[row.enrichmentSource],
                              background: SOURCE_COLOR[row.enrichmentSource] + "22"
                            }}>
                              {SOURCE_LABEL[row.enrichmentSource]}
                            </span>
                          )}
                          {row.enrichmentConfidence && row.enrichmentConfidence !== "LOW" && row.suggestedCategoryId && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, flexShrink: 0,
                              color: CONF_COLOR[row.enrichmentConfidence] || "#6b7280",
                              background: (CONF_COLOR[row.enrichmentConfidence] || "#6b7280") + "22"
                            }}>
                              {row.enrichmentConfidence}
                            </span>
                          )}
                          {row.suggestedCategoryName && !catId && (
                            <span style={{ fontSize: 11, color: "#8b5cf6" }}>
                              → {row.suggestedCategoryName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right: category dropdown */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        {isConfirmed && <span style={{ fontSize: 14, color: "#22c55e" }}>✓</span>}
                        <select
                          value={catId || ""}
                          onChange={e => setRowCategory(row.transactionId, e.target.value)}
                          style={{
                            fontSize: 12, padding: "4px 6px", borderRadius: 6, minWidth: 140,
                            border: catId
                              ? "1px solid rgba(34,197,94,0.4)"
                              : "1px solid rgba(255,255,255,0.12)",
                            background: "var(--surface, #1e1e2e)", color: catId ? "inherit" : "#6b7280"
                          }}>
                          <option value="">— Set category —</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.categoryName}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              paddingTop: 14, marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                {confirmedCount === 0
                  ? "Select a category for each transaction, or accept AI suggestions."
                  : <><strong style={{ color: "#22c55e" }}>{confirmedCount}</strong> transaction{confirmedCount !== 1 ? "s" : ""} ready to save.</>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={onClose}>Cancel</button>
                {confirmedCount > 0 && (
                  <button className="btn btn-primary" onClick={handleApply} disabled={applying}>
                    {applying ? "Saving…" : `Save ${confirmedCount}`}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Inline merchant name editor ───────────────────────────────────────────────
function MerchantInput({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => { setEditing(false); onChange(draft); };

  if (editing) {
    return (
      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        style={{ fontSize: 11, padding: "1px 4px", borderRadius: 4, border: "1px solid #6366f1",
          background: "transparent", color: "inherit", width: 130 }}
        autoFocus
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      title="Click to edit merchant name"
      style={{ fontSize: 11, cursor: "pointer", borderBottom: "1px dashed #4b5563",
        color: value ? "#94a3b8" : "#4b5563", whiteSpace: "nowrap",
        overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>
      {value || "Set merchant…"}
    </span>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", minWidth: 60 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{label}</div>
    </div>
  );
}

function truncate(s, max) {
  if (!s) return "";
  return s.length <= max ? s : s.substring(0, max) + "…";
}

// ── Styles ────────────────────────────────────────────────────────────────────
const overlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalBox = { background: "var(--surface, #1e1e2e)",
  border: "1px solid var(--border, rgba(255,255,255,0.12))",
  borderRadius: 12, padding: 24, maxWidth: 860, width: "96vw", maxHeight: "93vh", overflowY: "auto" };
const closeBtn = { background: "none", border: "none", fontSize: 22, cursor: "pointer",
  color: "var(--text-muted)", lineHeight: 1 };
const filterBtn = { fontSize: 11, padding: "4px 10px", borderRadius: 6, cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.12)", background: "transparent", color: "var(--text-muted)" };
const filterBtnActive = { borderColor: "#6366f1", color: "#818cf8", background: "rgba(99,102,241,0.12)" };
